import React, { useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Dropdown, Tooltip } from "antd"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import BlackoutScopeTag from "ui/Blackout/BlackoutScopeTag"
import BookingsActionAlert from "ui/Blackout/BookingsActionAlert"
import { includesId } from "helpers/blackouts"
import { BLACKOUT_SCOPES, SCOPE_TAG } from "../constants"
import { flattenTargetOptions, formatRowDateLabel } from "../utils"
import styles from "./BlackoutRow.module.css"

/** Scopes whose summary counts unavailable resources alongside the affected listings. */
const RESOURCE_SCOPES = [BLACKOUT_SCOPES.CREW, BLACKOUT_SCOPES.EQUIPMENT]

/** A stable empty fallback: a fresh `[]` from the selector would re-render the row every action. */
const NO_TARGETS = []

/**
 * The affects line names what is unavailable, not the generic category — "3 crew members
 * unavailable", as Figma 6372:10184 shows, rather than "3 resources unavailable".
 */
const RESOURCE_COUNT_COPY = {
  [BLACKOUT_SCOPES.CREW]: {
    single: { key: "settings.blackout.crew_count_single", fallback: "1 crew member" },
    plural: { key: "settings.blackout.crew_count", fallback: "{{count}} crew members" }
  },
  [BLACKOUT_SCOPES.EQUIPMENT]: {
    single: { key: "settings.blackout.equipment_count_single", fallback: "1 equipment item" },
    plural: { key: "settings.blackout.equipment_count", fallback: "{{count}} equipment items" }
  }
}

/**
 * One blackout record as a row.
 *
 * `variant` is the ground it sits on: "flat" is the divider-separated row the Business card
 * uses, "card" the white card the tinted Listing and Resources sections use. Phones render
 * both as cards — the section shell is gone there, so the row has to carry its own frame.
 */
const BlackoutRow = ({ blackout, menuItems, onReviewConflicts, variant = "flat" }) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)
  const reasonTextRef = useRef(null)
  const [isReasonTruncated, setIsReasonTruncated] = useState(false)

  /**
   * The chip ellipsises a long reason, so a tooltip is the only way to read the rest — but one
   * that merely repeats a reason already shown in full is noise. Measured on hover rather than
   * on render because how much fits depends on the card's width, which moves with the viewport.
   */
  const handleReasonEnter = () => {
    const node = reasonTextRef.current
    setIsReasonTruncated(Boolean(node) && node.scrollWidth > node.clientWidth)
  }

  const { scope, reason, recurring_yearly: isRecurringYearly, impact, target_ids: targetIds } = blackout
  // The crew list also carries crew members' own time off, edited from their profile
  const isTimeOff = blackout.entry_type === "time_off"
  // A weekly time off applies only on its selected weekdays: "monday" -> "Mon"
  const weekdaysLabel = (blackout.weekdays || []).map((day) => day.charAt(0).toUpperCase() + day.slice(1, 3)).join(", ")
  const listingsCount = impact?.listings_count || 0
  const resources = impact?.resources || []
  const conflictingCount = impact?.conflicting_bookings_count || 0
  const isResourceScope = RESOURCE_SCOPES.includes(scope)
  const isBusinessScope = scope === BLACKOUT_SCOPES.BUSINESS

  /**
   * The listing catalogue, already in the store: the page's filter row fetches every scope's
   * targets, and the create/edit form warms the same cache.
   */
  const listingTargets = useSelector((state) => state.blackouts?.targets?.data?.[BLACKOUT_SCOPES.LISTING] || NO_TARGETS)

  /**
   * The listings to itemise when the row is open.
   *
   * `impact.listings` is the source wherever the server sends it. It does not always: business
   * records may carry only a count, and a listing record always names its own targets anyway — so
   * both fall back to resolving ids against the catalogue rather than expanding into an empty
   * panel. A crew or equipment record has no such fallback: which listings a resource takes down
   * is a cascade only the server can compute.
   */
  const listings = useMemo(() => {
    const impactListings = impact?.listings || []
    if (impactListings.length) return impactListings

    const catalogue = flattenTargetOptions(listingTargets).map((option) => ({ id: option.id, title: option.label }))
    if (isBusinessScope) return catalogue
    if (scope === BLACKOUT_SCOPES.LISTING) return catalogue.filter((listing) => includesId(targetIds || [], listing.id))
    return []
  }, [impact, listingTargets, isBusinessScope, scope, targetIds])

  /**
   * "View" opens the row rather than leaving the page, for every scope (Figma 6372:10172).
   * A row with nothing to itemise — no listings and no resources — offers no toggle at all.
   */
  const hasDetails = isBusinessScope || listingsCount > 0 || resources.length > 0

  /**
   * Singular and plural are picked by hand rather than by i18next's plural suffixes: the project
   * is on i18next 19 with the v3 JSON format, where the two conventions already present in
   * `en.json` (`_one`/`_other` and `_plural`) do not both resolve.
   */
  const listingsText =
    listingsCount === 1
      ? t("settings.blackout.listing_affected_single", { defaultValue: "1 listing affected" })
      : t("settings.blackout.listings_affected", { defaultValue: "{{count}} listings affected", count: listingsCount })

  const hiddenListingsCount = Math.max(listingsCount - listings.length, 0)

  /** "Crew" / "Equipment" — the resource section is headed by the scope's own noun. */
  const resourceSectionTitle = t(SCOPE_TAG[scope]?.labelKey, { defaultValue: SCOPE_TAG[scope]?.labelDefault })

  /** A titled bullet list — the shape both halves of the expanded panel share. */
  const renderSection = (title, items) => (
    <div className={styles.detailsSection}>
      <Text variant="caption" size="md" fw="500" className={styles.detailsTitle}>
        {title}
      </Text>
      <ul className={styles.detailsList}>
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  )

  const getAffectedLabel = () => {
    // A business blackout closes everything by definition, so it states that rather than a
    // count the endpoint would have to compute.
    if (isBusinessScope) return t("settings.blackout.all_listings_affected", { defaultValue: "All listings affected" })
    if (isResourceScope) {
      const countCopy = RESOURCE_COUNT_COPY[scope][resources.length === 1 ? "single" : "plural"]
      const resourcesText = t(countCopy.key, { defaultValue: countCopy.fallback, count: resources.length })
      return t("settings.blackout.resources_affected", {
        defaultValue: "{{resources}} unavailable → {{listings}}",
        resources: resourcesText,
        listings: listingsText
      })
    }
    return listingsText
  }

  return (
    <article className={`${styles.row} ${styles[variant]}`}>
      <div className={styles.topRow}>
        <div className={styles.dateWrap}>
          <Text variant="sub" size="md" fw="500" className={styles.dateText}>
            {formatRowDateLabel(blackout)}
            {weekdaysLabel && ` · ${weekdaysLabel}`}
          </Text>
          {isRecurringYearly && (
            <span
              className={styles.repeatPill}
              title={t("settings.blackout.repeats_yearly", { defaultValue: "Repeats each year" })}
              aria-label={t("settings.blackout.repeats_yearly", { defaultValue: "Repeats each year" })}
            >
              <Icon name="Repeat" width={14} height={14} color="var(--color-primary)" />
            </span>
          )}
        </div>

        {/* Every scope carries the same dark pill (Figma 6254:112491) — scope glyph plus the scope
            spelled out, "Equipment Blackout" rather than the bare noun. */}
        <BlackoutScopeTag
          scope={scope}
          tone="solid"
          label={isTimeOff ? t("settings.blackout.tag_crew_time_off_solid", { defaultValue: "Crew Time Off" }) : undefined}
          className={styles.tag}
        />

        {menuItems.length > 0 && (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight">
            <button type="button" className={styles.menuButton} aria-label={t("settings.blackout.row_actions", { defaultValue: "Blackout actions" })}>
              <Icon name="MoreHorizontal" width={18} height={18} color="var(--color-text-secondary-dark)" />
            </button>
          </Dropdown>
        )}
      </div>

      {reason && (
        <Tooltip title={isReasonTruncated ? reason : ""} placement="topLeft">
          <span className={styles.reasonChip} onMouseEnter={handleReasonEnter}>
            <Icon name="Clipboard" width={14} height={14} color="var(--color-text-secondary-dark)" />
            <span ref={reasonTextRef} className={styles.reasonText}>
              {reason}
            </span>
          </span>
        </Tooltip>
      )}

      <div className={styles.affectsRow}>
        <Text variant="caption" size="lg" className={styles.affectsText}>
          {getAffectedLabel()}
        </Text>
        {/* Expanded, the toggle moves to the foot of the detail block, where the design puts it. */}
        {hasDetails && !isExpanded && (
          <button type="button" className={styles.linkButton} onClick={() => setIsExpanded(true)} aria-expanded={false}>
            {t("settings.blackout.view", { defaultValue: "View" })}
          </button>
        )}
      </div>

      {isExpanded && (
        <div className={styles.details}>
          {isResourceScope && resources.length > 0 && renderSection(resourceSectionTitle, resources.map((resource) => resource.label))}

          <div className={styles.detailsSection}>
            <Text variant="caption" size="md" fw="500" className={styles.detailsTitle}>
              {t("settings.blackout.listings_section", { defaultValue: "Listings" })}
            </Text>
            {listings.length > 0 ? (
              <ul className={styles.detailsList}>
                {listings.map((listing) => (
                  <li key={listing.id}>{listing.title}</li>
                ))}
                {/* `impact.listings` is paginated, so the list can be short of the count it claims. */}
                {hiddenListingsCount > 0 && (
                  <li className={styles.detailsMore}>
                    {t("settings.blackout.and_more_listings", { defaultValue: "+{{count}} more", count: hiddenListingsCount })}
                  </li>
                )}
              </ul>
            ) : (
              // Business scope reports a count without the titles behind it, and any scope can
              // come back that way — say what is affected rather than showing an empty section.
              <Text variant="caption" size="lg" className={styles.detailsFallback}>
                {getAffectedLabel()}
              </Text>
            )}
          </div>

          <button type="button" className={styles.linkButton} onClick={() => setIsExpanded(false)} aria-expanded>
            {t("settings.blackout.collapse", { defaultValue: "Collapse" })}
          </button>
        </div>
      )}

      <BookingsActionAlert
        count={conflictingCount}
        onReview={() => onReviewConflicts(blackout)}
        className={styles.alert}
      />
    </article>
  )
}

BlackoutRow.propTypes = {
  blackout: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    scope: PropTypes.string,
    entry_type: PropTypes.oneOf(["blackout", "time_off"]),
    weekdays: PropTypes.arrayOf(PropTypes.string),
    reason: PropTypes.string,
    recurring_yearly: PropTypes.bool,
    entries: PropTypes.array,
    impact: PropTypes.object
  }).isRequired,
  menuItems: PropTypes.array.isRequired,
  onReviewConflicts: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(["flat", "card"])
}

export default BlackoutRow
